import type { ManagementFilters, ManagementReportKey, Period, StudentStage } from '../../api/managementReports';
import { MANAGEMENT_REPORT_KEYS } from '../../api/managementReports';

/** Management Reports filters live in the URL (?report=…&period=…), so a
 * figure clicked on the Executive Overview opens its report with the same
 * filters, and a page can be bookmarked or shared. */

const PERIODS: Period[] = ['1m', '2m', '3m', '6m', '1y', 'custom', 'all'];
const STAGES: StudentStage[] = ['ACTIVE', 'DROPPED_OUT', 'IN_BATCH', 'AWAITING_DEPARTURE', 'DEPARTED'];
const NUMERIC = ['branchId', 'countryId', 'universityId', 'courseId', 'intakeId', 'counselorId', 'leadStatusId'] as const;
const DAYS = ['from', 'to', 'dropoutFrom', 'dropoutTo'] as const;

export const DEFAULT_PERIOD: Period = '6m';

/** Reports built but not shown yet (KPI workbook reports, 2026-11-05): no
 * tab, and a link or bookmarked URL opens the Executive Overview instead.
 * Remove a key to show its tab. The API still serves them. */
export const HIDDEN_REPORTS: ManagementReportKey[] = ['target-revenue', 'programme-performance', 'targets', 'revenue', 'fis', 'data-quality'];
export const isVisibleReport = (key: string): key is ManagementReportKey =>
  (MANAGEMENT_REPORT_KEYS as readonly string[]).includes(key) && !HIDDEN_REPORTS.includes(key as ManagementReportKey);

export const readReport = (params: URLSearchParams): ManagementReportKey => {
  const r = params.get('report');
  return r && isVisibleReport(r) ? r : 'executive';
};

export const readFilters = (params: URLSearchParams): ManagementFilters => {
  const period = params.get('period');
  const filters: ManagementFilters = { period: PERIODS.includes(period as Period) ? (period as Period) : DEFAULT_PERIOD };
  for (const key of NUMERIC) {
    const n = Number(params.get(key));
    if (Number.isInteger(n) && n > 0) filters[key] = n;
  }
  for (const key of DAYS) {
    const d = params.get(key);
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) filters[key] = d;
  }
  const stage = params.get('stage');
  if (STAGES.includes(stage as StudentStage)) filters.stage = stage as StudentStage;
  return filters;
};

export const writeParams = (report: ManagementReportKey, filters: ManagementFilters) => {
  const params = new URLSearchParams({ report });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '' && value !== null) params.set(key, String(value));
  }
  return params;
};

/** Which shared filters make sense for each report. */
export const REPORT_FILTERS: Record<ManagementReportKey, Array<'counselor' | 'leadStatus' | 'stage' | 'dropoutPeriod'>> = {
  executive: ['counselor'],
  'target-revenue': [],
  'programme-performance': ['counselor', 'stage'],
  'intake-performance': ['counselor', 'stage'],
  targets: ['counselor', 'stage'],
  revenue: ['counselor', 'stage'],
  fis: ['counselor', 'stage'],
  'lead-conversion': ['counselor', 'leadStatus'],
  dropouts: ['counselor', 'dropoutPeriod'],
  'counsellor-performance': ['counselor', 'leadStatus'],
  'university-performance': ['counselor', 'stage'],
  'data-quality': [],
};

/** Reports whose period picks intakes (by start month) rather than registrations. */
export const INTAKE_BASED: ManagementReportKey[] = ['programme-performance', 'intake-performance', 'targets', 'revenue', 'fis'];
/** Reports that show the current records and ignore the period. */
export const NO_PERIOD: ManagementReportKey[] = ['data-quality', 'target-revenue'];
/** Reports laid out as a fixed sheet: only the branch filter applies. */
export const BRANCH_ONLY: ManagementReportKey[] = ['target-revenue'];

export const PERIOD_LABELS: Record<Period, string> = {
  '1m': '1 month',
  '2m': '2 months',
  '3m': '3 months',
  '6m': '6 months',
  '1y': '1 year',
  custom: 'Custom range',
  all: 'All time',
};

export const STAGE_LABELS: Record<StudentStage, string> = {
  ACTIVE: 'Active (not dropped out)',
  DROPPED_OUT: 'Dropped out',
  IN_BATCH: 'Allocated to a batch',
  AWAITING_DEPARTURE: 'Awaiting departure',
  DEPARTED: 'Departed',
};
